import { Fragment, useEffect, useState } from "react";
import EmailGate from "../components/EmailGate";
import { hasStoredContentAccess } from "../lib/gatedContentTracking";

const FEES_ACCESS_KEY = "asha_os_fees_comparison_access";

const STYLES = `
.pms-quote-doc {
  --paper: #fcfcfd;
  --paper-2: #f4f5f8;
  --ink: #1c2540;
  --ink-2: #404a66;
  --ink-3: #737d96;
  --line: #d8dde6;
  --line-2: #e7eaf0;
  --accent: #3a5bd9;
  --accent-deep: #2638b2;
  --accent-soft: #eef0fa;
  --accent-ink: #2c43c4;
  --accent-soft-2: #eaedf7;
  --accent-border: #bdc6e8;

  font-family: 'Inter', system-ui, sans-serif;
  color: var(--ink);
  font-size: 14px;
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  font-feature-settings: 'ss01', 'cv11';
}

.pms-quote-doc *,
.pms-quote-doc *::before,
.pms-quote-doc *::after { box-sizing: border-box; }

.pms-quote-doc .page {
  max-width: 1040px;
  margin: 56px auto;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 14px;
  box-shadow:
    0 1px 0 rgba(0,0,0,0.02),
    0 30px 60px -30px rgba(20,30,40,0.18),
    0 12px 24px -16px rgba(20,30,40,0.10);
  overflow: hidden;
}

.pms-quote-doc .topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 56px;
  border-bottom: 1px solid var(--line);
  background: var(--paper);
}
.pms-quote-doc .brand {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Inter Tight', sans-serif;
  font-weight: 600;
  font-size: 16px;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.pms-quote-doc .brand-mark {
  width: 28px; height: 28px;
  border-radius: 7px;
  overflow: hidden;
  flex-shrink: 0;
}
.pms-quote-doc .brand-mark img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}
.pms-quote-doc .brand-meta {
  color: var(--ink-3);
  font-weight: 450;
}
.pms-quote-doc .topbar-right {
  display: flex;
  gap: 28px;
  align-items: center;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-3);
}
.pms-quote-doc .pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent-ink);
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.pms-quote-doc .pill::before {
  content: "";
  width: 6px; height: 6px;
  border-radius: 99px;
  background: var(--accent);
  box-shadow: 0 0 0 3px rgba(238, 240, 250, 0.7);
}

.pms-quote-doc .hero {
  padding: 72px 56px 56px;
  border-bottom: 1px solid var(--line);
  position: relative;
  background:
    radial-gradient(1200px 320px at 90% -100px, rgba(216, 222, 248, 0.55), transparent 60%),
    var(--paper);
}
.pms-quote-doc .eyebrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 18px;
}
.pms-quote-doc .eyebrow b { color: var(--accent-ink); font-weight: 500; }
.pms-quote-doc h1.title {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 56px;
  line-height: 1.04;
  letter-spacing: -0.035em;
  margin: 0 0 14px;
  color: var(--ink);
  max-width: 18ch;
}
.pms-quote-doc .title-sub {
  font-family: 'Fraunces', serif;
  font-style: italic;
  font-weight: 300;
  font-size: 24px;
  letter-spacing: -0.01em;
  color: var(--accent-ink);
  margin: 0 0 26px;
}
.pms-quote-doc .lede {
  font-size: 17px;
  line-height: 1.55;
  color: var(--ink-2);
  max-width: 62ch;
  margin: 0 0 48px;
  text-wrap: pretty;
}

.pms-quote-doc .meta-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  margin-top: 8px;
}
.pms-quote-doc .meta-foot {
  margin: 14px 0 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--ink-3);
  font-style: italic;
}
.pms-quote-doc .meta-cell {
  padding: 18px 0;
  border-right: 1px solid var(--line-2);
}
.pms-quote-doc .meta-cell:first-child { padding-right: 36px; }
.pms-quote-doc .meta-cell:not(:first-child) { padding-left: 36px; padding-right: 36px; }
.pms-quote-doc .meta-cell:last-child { border-right: 0; padding-right: 0; }
.pms-quote-doc .meta-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 8px;
}
.pms-quote-doc .meta-value {
  font-family: 'Inter Tight', sans-serif;
  font-size: 17px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.pms-quote-doc section.s {
  padding: 64px 56px;
  border-bottom: 1px solid var(--line);
}
.pms-quote-doc section.s:last-of-type { border-bottom: 0; }
.pms-quote-doc .s-head {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 32px;
  margin-bottom: 36px;
  align-items: baseline;
}
.pms-quote-doc .s-num {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-3);
  padding-top: 10px;
  border-top: 1px solid var(--ink);
  width: 52px;
}
.pms-quote-doc .s-title {
  font-family: 'Inter Tight', sans-serif;
  font-size: 34px;
  font-weight: 500;
  letter-spacing: -0.025em;
  line-height: 1.1;
  color: var(--ink);
  margin: 0;
}
.pms-quote-doc .s-sub {
  font-size: 15px;
  color: var(--ink-2);
  margin-top: 10px;
  max-width: 60ch;
  text-wrap: pretty;
}
.pms-quote-doc .s-body {
  margin-left: 152px;
}

.pms-quote-doc .incl-grid {
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: 0;
  border-top: 1px solid var(--line);
}
.pms-quote-doc .incl-col {
  padding: 28px 32px 28px 0;
  border-right: 1px solid var(--line-2);
}
.pms-quote-doc .incl-col + .incl-col {
  padding-left: 32px;
  padding-right: 0;
  border-right: 0;
}
.pms-quote-doc .incl-col-head {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 16px;
  row-gap: 2px;
  align-items: center;
  margin-bottom: 22px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--line-2);
}
.pms-quote-doc .incl-count {
  grid-row: 1 / span 2;
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 38px;
  letter-spacing: -0.03em;
  line-height: 1;
  color: var(--ink);
}
.pms-quote-doc .incl-count.accent { color: var(--accent); }
.pms-quote-doc .incl-col-title {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 22px;
  letter-spacing: -0.015em;
  color: var(--ink);
  white-space: nowrap;
  align-self: end;
}
.pms-quote-doc .incl-col-sub {
  font-size: 12.5px;
  color: var(--ink-3);
  align-self: start;
}

.pms-quote-doc .prod-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.pms-quote-doc .prod-list li {
  padding-bottom: 14px;
  border-bottom: 1px dashed var(--line-2);
}
.pms-quote-doc .prod-list li:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}
.pms-quote-doc .prod-name {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 14.5px;
  letter-spacing: -0.005em;
  color: var(--ink);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.pms-quote-doc .prod-sub {
  font-size: 12px;
  color: var(--ink-3);
  line-height: 1.5;
}
.pms-quote-doc .soon {
  display: inline-flex;
  padding: 1px 7px;
  border-radius: 4px;
  background: var(--accent-soft);
  color: var(--accent-ink);
  font-family: 'JetBrains Mono', monospace;
  font-size: 9.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 500;
}

.pms-quote-doc .agent-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  counter-reset: agent;
}
.pms-quote-doc .agent-list li {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 13.5px;
  letter-spacing: -0.005em;
  color: var(--ink);
  padding: 11px 14px 11px 28px;
  border-bottom: 1px solid var(--line-2);
  position: relative;
  counter-increment: agent;
}
.pms-quote-doc .agent-list li::before {
  content: counter(agent, decimal-leading-zero);
  position: absolute;
  left: 0;
  top: 12px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: var(--ink-3);
  font-weight: 400;
  letter-spacing: 0.04em;
}
.pms-quote-doc .agent-list li:nth-last-child(-n+2) { border-bottom: 0; }
.pms-quote-doc .agent-list li:nth-child(odd) { padding-right: 18px; }

.pms-quote-doc .piecemeal-line {
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  line-height: 1.6;
  color: var(--ink-2);
  margin: 0 0 28px;
  padding: 0 0 18px;
  border-bottom: 1px solid var(--line);
  text-wrap: pretty;
}
.pms-quote-doc .piecemeal-line b {
  color: var(--ink);
  font-weight: 600;
}
.pms-quote-doc .piecemeal-line .piecemeal-num {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--accent-ink);
}
.pms-quote-doc .piecemeal-line.bottom {
  margin: 32px 0 0;
  padding: 20px 0 0;
  border-bottom: 0;
  border-top: 1px solid var(--ink);
}

.pms-quote-doc .vendor-s {
  background: linear-gradient(180deg, var(--paper) 0%, #f7f7fb 100%);
}
.pms-quote-doc .vendor-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 4px;
}
.pms-quote-doc .vendor-table thead th {
  text-align: left;
  padding: 0 0 14px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  font-weight: 500;
  border-bottom: 1px solid var(--ink);
}
.pms-quote-doc .vendor-table thead th.vt-price { text-align: right; padding-right: 24px; }
.pms-quote-doc .vendor-table thead th.vt-asha-h { text-align: right; padding-right: 0; color: var(--accent-ink); }
.pms-quote-doc .vendor-table tbody td {
  padding: 18px 0;
  border-bottom: 1px solid var(--line-2);
  vertical-align: top;
}
.pms-quote-doc .vendor-table .vt-cat { width: 30%; padding-right: 20px; }
.pms-quote-doc .vendor-table .vt-vendor { width: 30%; padding-right: 20px; color: var(--ink-2); font-size: 13px; }
.pms-quote-doc .vendor-table .vt-price {
  width: 22%;
  text-align: right;
  padding-right: 24px;
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 16px;
  letter-spacing: -0.015em;
  color: var(--ink);
  white-space: nowrap;
}
.pms-quote-doc .vendor-table .vt-price small {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  font-weight: 400;
  color: var(--ink-3);
  margin-top: 2px;
  letter-spacing: 0;
}
.pms-quote-doc .vendor-table .vt-asha {
  width: 18%;
  text-align: right;
  padding-right: 0;
  white-space: nowrap;
}
.pms-quote-doc .vendor-table .vt-name {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 14.5px;
  letter-spacing: -0.005em;
  color: var(--ink);
}
.pms-quote-doc .vendor-table .vt-meta {
  font-size: 11.5px;
  color: var(--ink-3);
  margin-top: 3px;
  line-height: 1.45;
}
.pms-quote-doc .vendor-table tr:last-child td { border-bottom: 0; }

.pms-quote-doc .vendor-table tr.vt-group td {
  padding: 32px 0 12px;
  border-bottom: 1px solid var(--ink);
  font-family: 'Inter Tight', sans-serif;
  font-size: 17px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.pms-quote-doc .vendor-table tr.vt-group:first-child td { padding-top: 8px; }
.pms-quote-doc .vendor-table tr.vt-group .vt-group-num {
  display: inline-block;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-3);
  font-weight: 500;
  margin-right: 14px;
  vertical-align: middle;
}
.pms-quote-doc .vendor-table tr.vt-group + tr td { padding-top: 22px; }

.pms-quote-doc .vt-incl {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 4px 9px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent-ink);
  font-family: 'Inter Tight', sans-serif;
  font-size: 12.5px;
  font-weight: 500;
  letter-spacing: -0.005em;
  border: 1px solid var(--accent-border);
}
.pms-quote-doc .vt-incl::before {
  content: "✓";
  font-weight: 700;
  font-size: 11px;
  line-height: 1;
}
.pms-quote-doc .vt-asha-price {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 14.5px;
  letter-spacing: -0.01em;
  color: var(--accent-ink);
  white-space: nowrap;
}
.pms-quote-doc .vt-asha-price small {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 400;
  color: var(--ink-3);
  margin-top: 2px;
  letter-spacing: 0;
}

.pms-quote-doc .vendor-foot {
  margin-top: 18px;
  font-size: 12px;
  color: var(--ink-3);
  line-height: 1.55;
  max-width: 80ch;
}
.pms-quote-doc .vendor-foot b { color: var(--ink); font-weight: 500; }

.pms-quote-doc .fineprint {
  list-style: none;
  padding: 22px 0 0;
  margin: 32px 0 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  border-top: 1px solid var(--line-2);
}
.pms-quote-doc .fineprint.no-divider {
  border-top: 0;
  padding-top: 0;
  margin-top: 0;
}
.pms-quote-doc .fineprint li {
  font-size: 12.5px;
  color: var(--ink-3);
  line-height: 1.6;
  max-width: 88ch;
  padding-left: 18px;
  position: relative;
}
.pms-quote-doc .fineprint li::before {
  content: "";
  position: absolute;
  left: 0; top: 9px;
  width: 4px; height: 4px;
  border-radius: 99px;
  background: var(--ink-3);
}
.pms-quote-doc .fineprint .fp-label {
  color: var(--ink);
  font-weight: 500;
  font-family: 'Inter Tight', sans-serif;
  margin-right: 4px;
}
.pms-quote-doc .fineprint b { color: var(--ink); font-weight: 500; }

.pms-quote-doc .only-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  border-top: 1px solid var(--line);
  counter-reset: only;
}
.pms-quote-doc .only-list li {
  padding: 28px 0 28px 56px;
  border-bottom: 1px solid var(--line-2);
  position: relative;
  counter-increment: only;
}
.pms-quote-doc .only-list li:last-child { border-bottom: 0; }
.pms-quote-doc .only-list li::before {
  content: counter(only, decimal-leading-zero);
  position: absolute;
  left: 0;
  top: 30px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  color: var(--ink-3);
  font-weight: 500;
}
.pms-quote-doc .only-name {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 20px;
  letter-spacing: -0.015em;
  line-height: 1.2;
  color: var(--ink);
  margin-bottom: 10px;
}
.pms-quote-doc .only-desc {
  font-size: 14.5px;
  color: var(--ink-2);
  line-height: 1.6;
  max-width: 78ch;
  text-wrap: pretty;
}

.pms-quote-doc .tail {
  padding: 22px 56px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--paper);
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-3);
  border-top: 1px solid var(--line);
}

@media (max-width: 900px) {
  .pms-quote-doc .page { margin: 24px; }
  .pms-quote-doc .hero,
  .pms-quote-doc section.s,
  .pms-quote-doc .topbar,
  .pms-quote-doc .tail { padding-left: 28px; padding-right: 28px; }
  .pms-quote-doc h1.title { font-size: 44px; }
  .pms-quote-doc .s-head { grid-template-columns: 1fr; gap: 12px; }
  .pms-quote-doc .s-body { margin-left: 0; }
  .pms-quote-doc .meta-grid { grid-template-columns: 1fr 1fr; }
  .pms-quote-doc .incl-grid { grid-template-columns: 1fr; }
  .pms-quote-doc .incl-col { padding: 24px 0; border-right: 0; border-bottom: 1px solid var(--line-2); }
  .pms-quote-doc .incl-col + .incl-col { padding-left: 0; }
  .pms-quote-doc .only-list li {
    padding: 24px 0 24px 44px;
  }
  .pms-quote-doc .only-list li::before { top: 26px; }
  .pms-quote-doc .only-name { font-size: 18px; }
  .pms-quote-doc .only-desc { font-size: 14px; }
}

@media (max-width: 720px) {
  .pms-quote-doc .page { margin: 14px; border-radius: 14px; }
  .pms-quote-doc .hero,
  .pms-quote-doc section.s,
  .pms-quote-doc .topbar,
  .pms-quote-doc .tail {
    padding-left: 20px; padding-right: 20px;
  }
  .pms-quote-doc .topbar { padding-top: 14px; padding-bottom: 14px; }
  .pms-quote-doc .hero { padding-top: 44px; padding-bottom: 36px; }
  .pms-quote-doc section.s { padding-top: 40px; padding-bottom: 40px; }

  .pms-quote-doc h1.title { font-size: 34px; max-width: none; }
  .pms-quote-doc .title-sub { font-size: 18px; margin-bottom: 20px; }
  .pms-quote-doc .lede { font-size: 15px; margin-bottom: 32px; }

  .pms-quote-doc .meta-grid { grid-template-columns: 1fr; }
  .pms-quote-doc .meta-cell {
    padding: 14px 0 !important;
    border-right: 0;
    border-bottom: 1px solid var(--line-2);
  }
  .pms-quote-doc .meta-cell:last-child { border-bottom: 0; }

  /* Stack the vendor-table into cards */
  .pms-quote-doc .vendor-table,
  .pms-quote-doc .vendor-table tbody,
  .pms-quote-doc .vendor-table tr,
  .pms-quote-doc .vendor-table td {
    display: block;
    width: auto !important;
  }
  .pms-quote-doc .vendor-table thead { display: none; }
  .pms-quote-doc .vendor-table tbody tr {
    padding: 18px 0;
    border-bottom: 1px solid var(--line-2);
  }
  .pms-quote-doc .vendor-table tbody tr:last-child { border-bottom: 0; }
  .pms-quote-doc .vendor-table tbody td {
    padding: 0 !important;
    border: 0 !important;
  }
  .pms-quote-doc .vendor-table tbody td + td { margin-top: 8px; }
  .pms-quote-doc .vendor-table .vt-price,
  .pms-quote-doc .vendor-table .vt-asha {
    text-align: left !important;
    padding-right: 0 !important;
  }
  .pms-quote-doc .vendor-table tr.vt-group {
    padding: 26px 0 8px !important;
    border-top: 1px solid var(--ink) !important;
    margin-top: 8px;
  }
  .pms-quote-doc .vendor-table tr.vt-group td {
    padding: 0 !important;
    border-bottom: 0 !important;
  }
  .pms-quote-doc .vendor-table tr.vt-group + tr {
    padding-top: 14px !important;
  }

  .pms-quote-doc .tail {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding-top: 16px; padding-bottom: 16px;
  }
}

@media print {
  @page {
    size: letter;
    margin: 0.3in;
  }
  html, body {
    background: #fff !important;
  }
  body.pms-quote-body { background: #fff !important; }
  .pms-quote-doc { font-size: 11px !important; }
  .pms-quote-doc .page {
    max-width: 100% !important;
    margin: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
  .pms-quote-doc .topbar { padding: 14px 24px; }
  .pms-quote-doc .hero { padding: 32px 24px 24px; }
  .pms-quote-doc h1.title { font-size: 32px; max-width: none; }
  .pms-quote-doc .title-sub { font-size: 16px; margin-bottom: 14px; }
  .pms-quote-doc .lede { margin-bottom: 20px; font-size: 12px; }
  .pms-quote-doc section.s { padding: 24px; }
  .pms-quote-doc .s-head {
    grid-template-columns: 1fr;
    gap: 8px;
    margin-bottom: 16px;
  }
  .pms-quote-doc .s-title { font-size: 22px; }
  .pms-quote-doc .s-body { margin-left: 0; }
  .pms-quote-doc .tail { padding: 12px 24px; }

  .pms-quote-doc .vendor-table tr.vt-group td { padding: 18px 0 8px; }
  .pms-quote-doc .vendor-table tbody td { padding: 12px 0; }
  .pms-quote-doc .vt-incl { padding: 2px 8px; font-size: 10.5px; }

  .pms-quote-doc .incl-grid { display: block; }
  .pms-quote-doc .incl-col {
    padding: 18px 0;
    border-right: 0;
    border-bottom: 1px solid var(--line-2);
  }
  .pms-quote-doc .incl-col:last-child { border-bottom: 0; }
  .pms-quote-doc .incl-col + .incl-col { padding-left: 0; }
  .pms-quote-doc .agent-list { grid-template-columns: 1fr 1fr 1fr; }

  .pms-quote-doc .meta-grid,
  .pms-quote-doc .vendor-table tr,
  .pms-quote-doc .fineprint li,
  .pms-quote-doc .prod-list li,
  .pms-quote-doc .agent-list li,
  .pms-quote-doc .only-list li {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .pms-quote-doc .prod-name,
  .pms-quote-doc .incl-col-head {
    break-after: avoid;
    page-break-after: avoid;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
`;

const COMP_GROUPS = [
  {
    label: "Practice management & front-office software",
    items: [
      {
        cap: "Practice Management System",
        capSub: "Charts, perio, ledgers, scheduling, treatment planning, clinical notes — the system of record.",
        vendors: "Dentrix Ascend · Eaglesoft · Open Dental",
        price: "$400 – $700",
        priceUnit: "/ mo / location",
        ashaType: "incl",
      },
      {
        cap: "IT support & server maintenance",
        capSub: "Server-based PMS (Dentrix, Eaglesoft) needs on-premise IT — backups, patching, hardware, remote support.",
        vendors: "Local MSP / IT contractor",
        price: "~$200",
        priceUnit: "/ mo (server-based PMS)",
        ashaType: "incl",
        ashaNote: "Not needed",
      },
      {
        cap: "Communication Suite",
        capSub: "VoIP phones, 2-way SMS, universal inbox, team chat, intelligent fax, digital forms.",
        vendors: "Weave · RevenueWell · NexHealth",
        price: "$700 – $1,200",
        priceUnit: "/ mo / location",
        ashaType: "incl",
      },
      {
        cap: "Marketing & Engagement",
        capSub: "SMS / email campaigns, automated workflows, clinically-aware segmentation, AI replies.",
        vendors: "Solutionreach · Lighthouse 360+ · RevenueWell",
        price: "$300 – $600",
        priceUnit: "/ mo / location",
        ashaType: "incl",
      },
      {
        cap: "Revenue Intelligence & Analytics",
        capSub: "KPI dashboards, collection-rate insights, revenue cycle reporting, performance metrics.",
        vendors: "Dental Intel · Practice by Numbers · Jarvis Analytics",
        price: "$300 – $500",
        priceUnit: "/ mo / location",
        ashaType: "incl",
      },
      {
        cap: "Insurance Verification (software)",
        capSub: "Eligibility lookups, benefits-breakdown ingestion, dual-source verification, real-time payer responses.",
        vendors: "Zuub · Airpay · Pearly",
        price: "$300 – $500",
        priceUnit: "/ mo / location",
        ashaType: "incl",
      },
      {
        cap: "eClaims Portal",
        capSub: "Built-in clearinghouse connectivity for electronic claim submission. Single login.",
        vendors: "VyneTrellis · DentalXChange",
        price: "$150 – $250",
        priceUnit: "/ mo / location",
        ashaType: "incl",
      },
    ],
  },
  {
    label: "AI agent layer",
    items: [
      {
        cap: "AI Receptionist (24/7 inbound)",
        capSub: "Answers every call, books, handles FAQs, routes hand-offs, voicemail triage, after-hours coverage.",
        vendors: "Dentina.ai · Arini",
        price: "$300 – $500",
        priceUnit: "/ mo / location",
        ashaType: "incl",
      },
      {
        cap: "AI Schedule Optimizer & Outbound Calls",
        capSub: "Fills cancellations, rebalances columns, runs outbound campaigns to fill open chair time.",
        vendors: "PatientDesk",
        price: "$1,000",
        priceUnit: "/ mo / location",
        ashaType: "incl",
      },
      {
        cap: "AI Hygiene Recall & Outbound Calls",
        capSub: "Identifies due / overdue patients, multi-channel outreach, books visits without staff dialing.",
        vendors: "PatientDesk · Recall Solutions",
        price: "$1,000",
        priceUnit: "/ mo / location",
        ashaType: "incl",
      },
      {
        cap: "AI Case Acceptance & Tx-Plan Follow-ups",
        capSub: "Texts / emails patients with unaccepted treatment, answers questions, books the visit.",
        vendors: "Codent · SmileSnap",
        price: "$500",
        priceUnit: "/ mo / location",
        ashaType: "incl",
      },
      {
        cap: "AI Scribe (clinical notes)",
        capSub: "Operatory audio → clinical notes. Provider review and sign-off.",
        vendors: "Toothfairy AI · DentalScribe",
        price: "~$300",
        priceUnit: "/ mo (2 providers)",
        ashaType: "incl",
      },
      {
        cap: "Voice Perio (voice-charted exams)",
        capSub: "Provider dictates pocket depths, bleeding, recession, mobility; values land in the perio chart in real time.",
        vendors: "Bola AI",
        price: "$100",
        priceUnit: "/ provider / month",
        ashaType: "incl",
      },
      {
        cap: "ePrescribe",
        capSub: "Electronic prescriptions integrated with the chart and patient timeline.",
        vendors: "DoseSpot",
        price: "~$200",
        priceUnit: "/ mo / location",
        ashaType: "incl",
        ashaNote: "Coming soon",
      },
    ],
  },
  {
    label: "Insurance & billing operations",
    items: [
      {
        cap: "Insurance Verification Calls",
        capSub: "Live calls to carriers for full benefit breakdowns when payer portals fall short.",
        vendors: "eAssist · Wisdom · DentalRev",
        price: "$2,400",
        priceUnit: "/ mo  ·  $8 / patient × 300 patients",
        ashaType: "incl",
      },
      {
        cap: "Claim Submission, Scrubbing & Denial Management",
        capSub: "Pre-submission scrubbing, submission, denial follow-up, and appeals — the bundled billing service.",
        vendors: "eAssist · Wisdom · DCS",
        price: "3.5%",
        priceUnit: "of collections",
        ashaType: "incl",
      },
      {
        cap: "AI EOB Posting",
        capSub: "Reads EOBs, posts adjustments, flags underpayments, opens appeals on the same agent stack.",
        vendors: "Lassie",
        price: "2%",
        priceUnit: "of collections",
        ashaType: "incl",
      },
      {
        cap: "Patient Billing",
        capSub: "Statement generation, text-to-pay, payment plans, collections sequencing, write-off rules.",
        vendors: "eAssist · Wisdom · Pearly",
        price: "$1,000 – $1,200",
        priceUnit: "/ mo / location",
        ashaType: "incl",
      },
      {
        cap: "Payment Reconciliation",
        capSub: "Matches deposits to claims, reconciles bank statements, surfaces unposted payments.",
        vendors: "eAssist · Wisdom",
        price: "$200",
        priceUnit: "/ mo / location",
        ashaType: "incl",
      },
    ],
  },
  {
    label: "Asha features for which we don't have a pricing comparison",
    items: [
      {
        cap: "ARIA",
        capSub: "The autopilot layer. Most dental AI is a chat window that gives you answers you still have to act on. ARIA takes the action — schedules, refiles, posts, calls — through the same systems your staff already uses.",
        vendors: "No equivalent on the market",
        price: "—",
        priceUnit: "",
        ashaType: "incl",
      },
      {
        cap: "AI Practice Manager (Copilot)",
        capSub: "Always-on operations copilot. Asks the manager-side question, runs the report, surfaces the leak, drafts the next decision — pulls from the same chart, schedule, and ledger every agent works in.",
        vendors: "No equivalent on the market",
        price: "—",
        priceUnit: "",
        ashaType: "incl",
      },
      {
        cap: "AI Fax Manager",
        capSub: "Inbound fax triage. Classifies referrals, EOBs, ROIs, treatment letters; OCRs the content; routes each fax to the right inbox; surfaces the action to take.",
        vendors: "No equivalent on the market",
        price: "—",
        priceUnit: "",
        ashaType: "incl",
      },
      {
        cap: "AI Treatment Planner",
        capSub: "Generates treatment plans from chart history, perio readings, and imaging context; sequences procedures by clinical priority; surfaces alternatives the provider can edit before presenting.",
        vendors: "No equivalent on the market",
        price: "—",
        priceUnit: "",
        ashaType: "incl",
      },
      {
        cap: "AI Patient Intake",
        capSub: "Mobile-first pre-visit forms. ID scan, insurance card capture, signed consents — parsed straight into the chart, no data re-entry on the front desk.",
        vendors: "No equivalent on the market",
        price: "—",
        priceUnit: "",
        ashaType: "incl",
      },
    ],
  },
];

const ONLY_IN_ASHA = [
  {
    name: "One platform that actually works as one",
    desc: "Stitching ten point solutions together is a tax — not just on the budget, but on every workflow. Different logins, fields one tool can read but another can't write to, integrations that drift the day a vendor ships a release. Asha is one product, one schema, one team. Recall, billing, scribes, and reception all read from and write to the same chart in real time, so the patient experience and the staff experience both feel seamless instead of stitched.",
  },
  {
    name: "Past the automation inflection point",
    desc: "There's a level of automation where the practice runs itself on most days — calls answered, schedules filled, claims submitted, EOBs posted, statements out — and the day stops being about chasing the front desk. When a team member calls out sick, the work doesn't stop. Growth stops feeling like a hiring problem; you don't need another admin or ops hire to handle more chairs. Asha is the only platform that gets a practice past that line, because it is not an isolated AI point solution but rather a full AI operating system with 20+ AI products that work together.",
  },
  {
    name: "AI-native, not AI-bolted-on",
    desc: "Most dental software has AI in the form of a chatbot or a predictive widget. Asha was built from the ground up so the AI can take actions — reschedule a column, refile a denial, post an EOB, run a recall campaign — directly through the same APIs your staff uses. We're the only AI-native PMS in dentistry; everyone else is retrofitting AI on top of software designed before LLMs existed.",
  },
];

const OsFeesComparisonPage = () => {
  const [isLocked, setIsLocked] = useState(true);

  useEffect(() => {
    if (
      hasStoredContentAccess(
        FEES_ACCESS_KEY,
        "asha_os_fees_comparison_session_id"
      )
    ) {
      setIsLocked(false);
    }
  }, []);

  const handleAccessGranted = (email) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(FEES_ACCESS_KEY, "true");
    }
    // eslint-disable-next-line no-console
    console.log("Pricing comparison access granted for:", email);
    setIsLocked(false);
  };

  useEffect(() => {
    const link1 = document.createElement("link");
    link1.rel = "preconnect";
    link1.href = "https://fonts.googleapis.com";

    const link2 = document.createElement("link");
    link2.rel = "preconnect";
    link2.href = "https://fonts.gstatic.com";
    link2.crossOrigin = "";

    const link3 = document.createElement("link");
    link3.rel = "stylesheet";
    link3.href =
      "https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=Inter:wght@400;450;500;600&family=JetBrains+Mono:wght@400;500&family=Fraunces:opsz,wght@9..144,300;9..144,400&display=swap";

    document.head.appendChild(link1);
    document.head.appendChild(link2);
    document.head.appendChild(link3);

    const previousBg = document.body.style.background;
    const previousMargin = document.body.style.margin;
    document.body.style.background = "#f1efe7";
    document.body.style.margin = "0";
    document.body.classList.add("pms-quote-body");
    document.title = "Asha — Pricing Comparison";

    return () => {
      document.head.removeChild(link1);
      document.head.removeChild(link2);
      document.head.removeChild(link3);
      document.body.style.background = previousBg;
      document.body.style.margin = previousMargin;
      document.body.classList.remove("pms-quote-body");
    };
  }, []);

  return (
    <div className="pms-quote-doc">
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      {isLocked && (
        <EmailGate
          onAccessGranted={handleAccessGranted}
          title="Please enter your email to view the pricing comparison"
          description={
            <>
              Access to the{" "}
              <span className="text-blue-400 font-semibold">Asha Health</span>{" "}
              pricing comparison is restricted.
            </>
          }
          submitLabel="View comparison"
          agreementNoun="this pricing comparison"
          sessionStorageKey="asha_os_fees_comparison_session_id"
        />
      )}

      <div className="page">
        {/* TOP BAR */}
        <div className="topbar">
          <div className="brand">
            <div className="brand-mark">
              <img src="/assets/alogo.png" alt="Asha" />
            </div>
            <span>Asha Health</span>
            <span className="brand-meta">· asha.dental</span>
          </div>
          <div className="topbar-right">
            <span className="pill">Confidential</span>
          </div>
        </div>

        {/* HERO */}
        <div className="hero">
          <div className="eyebrow">
            <b>Pricing Comparison</b> &nbsp;·&nbsp; Asha vs the piecemeal vendor stack
          </div>
          <h1 className="title">
            Asha Pricing Comparison
          </h1>
          <div className="title-sub">
            This document goes over what it would cost to piecemeal the Asha AI Operating System by combining other vendors — based on their published rates.
          </div>
          <p className="lede">
            This document compares Asha's all-in monthly fee against what it
            would cost to assemble equivalent coverage from the typical dental
            vendor stack — capability by capability. Vendor pricing reflects
            typical published prices and may vary by practice size, region,
            and contract length.
          </p>

          <div className="meta-grid">
            <div className="meta-cell">
              <div className="meta-label">Prepared by</div>
              <div className="meta-value">Asha Health, Inc.</div>
            </div>
            <div className="meta-cell">
              <div className="meta-label">Comparison valid through</div>
              <div className="meta-value">Q2 2026</div>
            </div>
          </div>
          <p className="meta-foot">
            Rack rates may shift; this comparison is updated quarterly.
          </p>
        </div>

        {/* § 01 — INCLUDED */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 01</div>
            <div>
              <h2 className="s-title">What's included with Asha</h2>
              <p className="s-sub">
                Every Asha plan ships with the full platform — the PMS, both
                AI agent suites, and our patient-facing communication and
                payments tooling.
              </p>
            </div>
          </div>
          <div className="s-body">
            <div className="incl-grid">
              <div className="incl-col">
                <div className="incl-col-head">
                  <span className="incl-count">10</span>
                  <span className="incl-col-title">Softwares</span>
                  <span className="incl-col-sub">
                    Full platform, no third-party stitching.
                  </span>
                </div>
                <ul className="prod-list">
                  <li>
                    <div className="prod-name">Practice Management System</div>
                    <div className="prod-sub">
                      Patient charts · Perio charting · Ledgers &amp; billing ·
                      Treatment planning · Scheduling · Clinical notes ·
                      Cloud-based · Multi-user access
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Communication Suite</div>
                    <div className="prod-sub">
                      VoIP phone system · Yealink desk phones · 2-way texting ·
                      Universal inbox · Team chat &amp; channels · Smart voice
                      logs · Intelligent fax · Next-gen digital forms
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Marketing Suite</div>
                    <div className="prod-sub">
                      SMS &amp; email campaigns · Automated workflows ·
                      Clinically-aware segmentation · AI handles patient
                      replies
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">eClaims Portal</div>
                    <div className="prod-sub">
                      Built-in eClaims · Clearinghouse connectivity · Single
                      login · Integrated submission
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Revenue Intelligence Suite</div>
                    <div className="prod-sub">
                      Reporting &amp; analytics dashboards · KPI tracking ·
                      Collection-rate insights · Revenue cycle analytics ·
                      Performance metrics
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Cloud Imaging Software</div>
                    <div className="prod-sub">
                      SOTA Cloud Imaging · DICOM compatible · Digital X-ray
                      viewing · Connects to your sensors · Cloud storage
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">
                      Insurance Verification Software
                    </div>
                    <div className="prod-sub">
                      Automated verification · Real-time eligibility · Benefits
                      breakdown upload · Dual-source verification
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Integrated Payments Suite</div>
                    <div className="prod-sub">
                      Text-to-pay · Card readers · Multi-method support ·
                      Automated reminders
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Patient Portal</div>
                    <div className="prod-sub">
                      Secure patient links · Interactive forms · Treatment-plan
                      viewing · Insurance info upload
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">
                      ePrescribe <span className="soon">Coming soon</span>
                    </div>
                    <div className="prod-sub">
                      Electronic prescriptions · Integrated with PMS
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Online Scheduling</div>
                    <div className="prod-sub">
                      Patient self-scheduling · Real-time availability
                    </div>
                  </li>
                </ul>
              </div>

              <div className="incl-col">
                <div className="incl-col-head">
                  <span className="incl-count accent">21</span>
                  <span className="incl-col-title">AI Agents</span>
                  <span className="incl-col-sub">
                    Front- and back-office automation, end-to-end.
                  </span>
                </div>
                <ul className="agent-list">
                  <li>AI Receptionist</li>
                  <li>AI Schedule Optimizer</li>
                  <li>AI Hygiene Recall</li>
                  <li>AI Patient Experience</li>
                  <li>AI Case Acceptance</li>
                  <li>AI Patient Intake</li>
                  <li>AI Practice Manager</li>
                  <li>AI Fax Manager</li>
                  <li>AI Insurance Verification</li>
                  <li>AI Claim Submission</li>
                  <li>AI Claim Scrubbing</li>
                  <li>AI Denial Management</li>
                  <li>AI Patient Billing</li>
                  <li>AI AR &amp; Collections</li>
                  <li>AI EOB Posting</li>
                  <li>AI Insurance Calls</li>
                  <li>AI Copilot</li>
                  <li>AI Scribe</li>
                  <li>AI Voice Perio</li>
                  <li>AI Treatment Planner</li>
                  <li>AI Revenue Analytics</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* § 02 — LINE-BY-LINE COMPARISON */}
        <section className="s vendor-s">
          <div className="s-head">
            <div className="s-num">§ 02</div>
            <div>
              <h2 className="s-title">Feature-by-feature pricing comparison</h2>
              <p className="s-sub">
                What each capability costs through typical vendors at rack
                rates, and how it's priced inside Asha.
              </p>
            </div>
          </div>

          <div className="s-body">
            <p className="piecemeal-line">
              <b>
                If you piecemealed the same scope from other vendors, you'd pay
                {" "}
                <span className="piecemeal-num">~$10,000 / mo</span>
                {" "}+{" "}
                <span className="piecemeal-num">3.5% of collections</span>
              </b>{" "}
              per location — and that's on the low end of what these vendors
              typically charge, before the staff time it takes to manage a
              dozen separate tools.
            </p>

            <table className="vendor-table">
              <thead>
                <tr>
                  <th className="vt-cat">Capability</th>
                  <th className="vt-vendor">Typical vendors</th>
                  <th className="vt-price">Typical price</th>
                  <th className="vt-asha-h">Asha</th>
                </tr>
              </thead>
              <tbody>
                {COMP_GROUPS.map((group, gi) => (
                  <Fragment key={gi}>
                    <tr className="vt-group">
                      <td colSpan={4}>
                        <span className="vt-group-num">
                          Group {String(gi + 1).padStart(2, "0")}
                        </span>
                        <span>{group.label}</span>
                      </td>
                    </tr>
                    {group.items.map((it, ii) => (
                      <tr key={`${gi}-${ii}`}>
                        <td className="vt-cat">
                          <div className="vt-name">{it.cap}</div>
                          {it.capSub && (
                            <div className="vt-meta">{it.capSub}</div>
                          )}
                        </td>
                        <td className="vt-vendor">{it.vendors}</td>
                        <td className="vt-price">
                          {it.price}
                          <small>{it.priceUnit}</small>
                        </td>
                        <td className="vt-asha">
                          {it.ashaType === "incl" ? (
                            <span className="vt-incl">
                              {it.ashaNote || "Included"}
                            </span>
                          ) : (
                            <div className="vt-asha-price">
                              {it.ashaPrice}
                              <small>{it.ashaPriceUnit}</small>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>

            <p className="piecemeal-line bottom">
              <b>
                The priced rows above add up to roughly{" "}
                <span className="piecemeal-num">$10,000 – $11,000 / mo</span>
                {" "}+{" "}
                <span className="piecemeal-num">3.5% of collections</span>
                {" "}per location at typical pricing.
              </b>{" "}
              Asha replaces the entire stack — software and billing service —
              with a single tiered fee, plus several capabilities no other
              vendor in dentistry offers at any price.
            </p>

            <p className="vendor-foot">
              <b>Methodology.</b> Vendor pricing reflects typical published
              prices for a single-location general practice. Outsourced
              billing service fees (eAssist, Wisdom, DCS) bundle submission,
              scrubbing, denial follow-up, and posting into a single
              percent-of-collections fee — broken out separately above so each
              capability is visible. Asha replaces the percent-of-collections
              model with a tiered AI Agents fee.
            </p>
          </div>
        </section>

        {/* § 03 — ONLY IN ASHA */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 03</div>
            <div>
              <h2 className="s-title">Beyond the line items</h2>
              <p className="s-sub">
                The numbers in §02 are real, but they're not the whole
                argument. Three things only happen when one vendor owns the
                chart, the schedule, the ledger, and the agents at the same
                time.
              </p>
            </div>
          </div>

          <div className="s-body">
            <ul className="only-list">
              {ONLY_IN_ASHA.map((item, i) => (
                <li key={i}>
                  <div className="only-name">{item.name}</div>
                  <div className="only-desc">{item.desc}</div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* TAIL */}
        <div className="tail">
          <span>Asha Health, Inc.</span>
          <span>
            Confidential — Comparison of typical commercial terms; not a
            binding agreement.
          </span>
          <span>Page 01 / 01</span>
        </div>
      </div>
    </div>
  );
};

export default OsFeesComparisonPage;
